import React, { useCallback, useMemo, useRef, useState } from 'react';
import { GoalBase } from '../types';

type GoalSelectionContextType = {
  selectedGoalIds: Set<string>;
  toggleGoal: (goalId: string) => void;
  selectAll: () => void;
  selectGoals: (goalIds: string[]) => void;
  deselectAll: () => void;
  isSelected: (goalId: string) => boolean;
  setSelectableGoals: (goals: GoalBase[]) => void;
  getSelectableGoals: () => GoalBase[];
};

const GoalSelectionContext = React.createContext<GoalSelectionContextType>({
  selectedGoalIds: new Set(),
  toggleGoal: () => {},
  selectAll: () => {},
  selectGoals: () => {},
  deselectAll: () => {},
  isSelected: () => false,
  setSelectableGoals: () => {},
  getSelectableGoals: () => [],
});

export const GoalSelectionProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [selectedGoalIds, setSelectedGoalIds] = useState<Set<string>>(
    new Set(),
  );
  // Use ref to avoid re-render loops: selectableGoals is set by a child
  // component (GoalsListWithFilter) and read imperatively by BulkApprovalBar
  const selectableGoalsRef = useRef<GoalBase[]>([]);

  const setSelectableGoals = useCallback((goals: GoalBase[]) => {
    selectableGoalsRef.current = goals;
  }, []);

  const getSelectableGoals = useCallback(() => {
    return selectableGoalsRef.current;
  }, []);

  const toggleGoal = useCallback((goalId: string) => {
    setSelectedGoalIds((prev) => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
      } else {
        next.add(goalId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedGoalIds(new Set(selectableGoalsRef.current.map((g) => g.id)));
  }, []);

  const selectGoals = useCallback((goalIds: string[]) => {
    setSelectedGoalIds((prev) => {
      const next = new Set(prev);
      for (const id of goalIds) {
        next.add(id);
      }
      return next;
    });
  }, []);

  const deselectAll = useCallback(() => {
    setSelectedGoalIds(new Set());
  }, []);

  const isSelected = useCallback(
    (goalId: string) => selectedGoalIds.has(goalId),
    [selectedGoalIds],
  );

  const value = useMemo(
    () => ({
      selectedGoalIds,
      toggleGoal,
      selectAll,
      selectGoals,
      deselectAll,
      isSelected,
      setSelectableGoals,
      getSelectableGoals,
    }),
    [
      selectedGoalIds,
      toggleGoal,
      selectAll,
      selectGoals,
      deselectAll,
      isSelected,
      setSelectableGoals,
      getSelectableGoals,
    ],
  );

  return (
    <GoalSelectionContext.Provider value={value}>
      {children}
    </GoalSelectionContext.Provider>
  );
};

export const useGoalSelection = () => React.useContext(GoalSelectionContext);
